import React, { useEffect, useState, useRef, useCallback } from 'react'
import Layout from '../../Components/Layout'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaStar, FaCar, FaShoppingCart, FaTrash, FaUsers, FaClock, FaMapMarkerAlt } from "react-icons/fa";
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import config from '../../config';
import Swal from 'sweetalert2';
import GoogleMapsLoader from '../../Components/Autocomplete/GoogleMapsLoader';
import TransferAutocomplete from '../../Components/Autocomplete/TransferAutocomplete';
import TransferListCard from '../../Components/Transfer/ListCard';
import transferService from '../../services/transferService';
import './TransferList.css';

export default function TransferList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    
    // API sonuçlarını location state'ten al
    const initialTransferResults = location.state?.transferResults;
    const initialSearchParams = location.state?.searchParams;
    
    const [transferList, setTransferList] = useState([]);
    const [prices, setPrices] = useState({
        min_price: 0,
        max_price: 500
    });
    const [rates, setRates] = useState({
        minRate: 0,
        maxRate: 500
    });
    const [selectedVehicleTypes, setSelectedVehicleTypes] = useState([]);
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const [formParams, setFormParams] = useState({
        type: searchParams.get('type') || 'one-way',
        pickup: searchParams.get('pickup') || '',
        dropoff: searchParams.get('dropoff') || '',
        date: searchParams.get('date') || '',
        returnDate: searchParams.get('returnDate') || '',
        adults: parseInt(searchParams.get('adults')) || 2,
        children: parseInt(searchParams.get('children')) || 0
    });
    
    // Transfer location states
    const [pickupLocation, setPickupLocation] = useState({
        name: searchParams.get('pickup') || '',
        formatted_address: '',
        lat: parseFloat(searchParams.get('pickupLat')) || null,
        lng: parseFloat(searchParams.get('pickupLng')) || null,
        place_id: '',
        locationType: searchParams.get('pickupType') || ''
    });
    
    const [dropoffLocation, setDropoffLocation] = useState({
        name: searchParams.get('dropoff') || '',
        formatted_address: '',
        lat: parseFloat(searchParams.get('dropoffLat')) || null,
        lng: parseFloat(searchParams.get('dropoffLng')) || null,
        place_id: '',
        locationType: searchParams.get('dropoffType') || ''
    });
    
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sortOption, setSortOption] = useState({type:'default', column:'default'});
    const [vehicleTypesLess, setVehicleTypesLess] = useState(true);
    const [featuresLess, setFeaturesLess] = useState(true);
    const debounceTimeout = useRef(null);
    const [apiTransferResults, setApiTransferResults] = useState(initialTransferResults || null);

    const [cartSummary, setCartSummary] = useState({
        totalItems: 0,
        items: [],
        totalPrice: 0
    });

    // Static data for transfers
    const staticTransfers = [
        {
            id: 1,
            name: "Premium Sedan Transfer",
            vehicleType: "Sedan",
            capacity: 3,
            luggage: 2,
            price: 45,
            currency: "EUR",
            duration: "45 min",
            pickup: formParams.pickup,
            dropoff: formParams.dropoff,
            image: "/assets/img/cars/car-06.jpg",
            features: ["Air Conditioning", "WiFi", "Professional Driver"],
            rating: 4.8,
            reviews: 245,
            provider: "Premium Transfers",
            description: "Comfortable sedan transfer with professional driver"
        },
        {
            id: 2,
            name: "Luxury SUV Transfer",
            vehicleType: "SUV",
            capacity: 6,
            luggage: 4,
            price: 75,
            currency: "EUR",
            duration: "45 min",
            pickup: formParams.pickup,
            dropoff: formParams.dropoff,
            image: "/assets/img/cars/car-07.jpg",
            features: ["Air Conditioning", "WiFi", "Professional Driver", "Leather Seats"],
            rating: 4.9,
            reviews: 189,
            provider: "Luxury Transfers",
            description: "Spacious SUV transfer with luxury amenities"
        },
        {
            id: 3,
            name: "Economy Transfer",
            vehicleType: "Hatchback",
            capacity: 2,
            luggage: 1,
            price: 25,
            currency: "EUR",
            duration: "50 min",
            pickup: formParams.pickup,
            dropoff: formParams.dropoff,
            image: "/assets/img/cars/car-08.jpg",
            features: ["Air Conditioning", "Professional Driver"],
            rating: 4.5,
            reviews: 156,
            provider: "Budget Transfers",
            description: "Affordable and reliable transfer service"
        },
        {
            id: 4,
            name: "Van Transfer",
            vehicleType: "Van",
            capacity: 8,
            luggage: 6,
            price: 95,
            currency: "EUR",
            duration: "50 min",
            pickup: formParams.pickup,
            dropoff: formParams.dropoff,
            image: "/assets/img/cars/car-09.jpg",
            features: ["Air Conditioning", "WiFi", "Professional Driver", "Extra Space"],
            rating: 4.7,
            reviews: 98,
            provider: "Group Transfers",
            description: "Perfect for groups and families with extra luggage"
        },
        {
            id: 5,
            name: "Business Class Transfer",
            vehicleType: "Executive",
            capacity: 3,
            luggage: 3,
            price: 120,
            currency: "EUR",
            duration: "40 min",
            pickup: formParams.pickup,
            dropoff: formParams.dropoff,
            image: "/assets/img/cars/car-10.jpg",
            features: ["Air Conditioning", "WiFi", "Professional Driver", "Leather Seats", "Refreshments"],
            rating: 5.0,
            reviews: 67,
            provider: "Executive Transfers",
            description: "Premium business class transfer with executive amenities"
        }
    ];

    const vehicleTypes = [
        { code: 'sedan', name: 'Sedan' },
        { code: 'suv', name: 'SUV' },
        { code: 'hatchback', name: 'Hatchback' },
        { code: 'van', name: 'Van' },
        { code: 'executive', name: 'Executive' }
    ];

    const features = [
        { code: 'ac', name: 'Air Conditioning' },
        { code: 'wifi', name: 'WiFi' },
        { code: 'leather', name: 'Leather Seats' },
        { code: 'refreshments', name: 'Refreshments' },
        { code: 'extra_space', name: 'Extra Space' }
    ];

    useEffect(() => {
        // Eğer API sonuçları varsa onları kullan
        if (apiTransferResults) {
            // API sonuçlarını transfer listesine dönüştür
            const convertedResults = convertApiResultsToTransferList(apiTransferResults);
            setTransferList(convertedResults);
            setIsLoading(false);
        } else {
            // API sonucu bekleniyor, boş liste göster
            setTransferList([]);
        }
    }, [apiTransferResults]);

    // URL parametrelerinden otomatik arama yap (sayfa açılırken)
    useEffect(() => {
        const autoSearch = async () => {
            // Eğer zaten API sonuçları varsa otomatik arama yapma
            if (apiTransferResults) return;
            
            // Gerekli parametreler var mı kontrol et
            const hasRequiredParams = pickupLocation.name && dropoffLocation.name && 
                                    pickupLocation.lat && pickupLocation.lng &&
                                    dropoffLocation.lat && dropoffLocation.lng &&
                                    formParams.date;
            
            if (hasRequiredParams) {
                setIsLoading(true);
                setError(null);
                
                try {
                    const searchParams = {
                        transferType: formParams.type,
                        fromCoordinates: {
                            lat: pickupLocation.lat,
                            lng: pickupLocation.lng
                        },
                        toCoordinates: {
                            lat: dropoffLocation.lat,
                            lng: dropoffLocation.lng
                        },
                        departureDate: formParams.date,
                        returnDate: formParams.type === 'round-trip' ? formParams.returnDate : null,
                        adults: formParams.adults,
                        children: formParams.children
                    };

                    const transferResults = await transferService.searchTransfers(searchParams);
                    setApiTransferResults(transferResults);
                    
                } catch (error) {
                    console.error('Auto-search error:', error);
                    setError(error.message);
                    setTransferList([]);
                    setIsLoading(false);
                }
            }
        };

        // Component mount olduktan sonra kısa bir delay ile otomatik arama yap
        const timer = setTimeout(autoSearch, 500);
        return () => clearTimeout(timer);
    }, []);

    // API sonuçlarını transfer list formatına dönüştür
    const convertApiResultsToTransferList = (apiResults) => {
        try {
            // Yeni API response yapısını kontrol et
            if (apiResults && apiResults.success && apiResults.data && 
                apiResults.data.results && Array.isArray(apiResults.data.results)) {
                
                // API'den gelen yeni formatı eski format'a uyarla
                return apiResults.data.results.map(transfer => ({
                    id: transfer.vehicleId,
                    vehicleId: transfer.vehicleId,
                    name: transfer.name,
                    vehicleType: transfer.vehicleType,
                    vehicleCategory: transfer.vehicleCategory,
                    vehicleCode: transfer.vehicleCode,
                    capacity: transfer.maxPassengers,
                    minPassengers: transfer.minPassengers,
                    maxPassengers: transfer.maxPassengers,
                    luggage: transfer.baggageCapacity,
                    price: parseFloat(transfer.pricing.totalPrice),
                    basePrice: parseFloat(transfer.pricing.basePrice),
                    currency: transfer.pricing.currency.code,
                    currencySymbol: transfer.pricing.currency.symbol,
                    image: transfer.thumbnail,
                    thumbnail: transfer.thumbnail,
                    pickup: transfer.routes && transfer.routes[0] ? transfer.routes[0].from.name : '',
                    dropoff: transfer.routes && transfer.routes[0] ? transfer.routes[0].to.name : '',
                    pickupAddress: transfer.routes && transfer.routes[0] ? transfer.routes[0].from.formatted_address : '',
                    dropoffAddress: transfer.routes && transfer.routes[0] ? transfer.routes[0].to.name : '',
                    brand: transfer.brand,
                    model: transfer.model,
                    routes: transfer.routes,
                    pricing: transfer.pricing,
                    dates: transfer.dates,
                    passengers: transfer.passengers,
                    offerKey: transfer.offerKey,
                    rating: 4.5,
                    reviews: 0,
                    provider: transfer.brand ? transfer.brand.name : 'Transfer Service',
                    description: `${transfer.brand?.name || ''} ${transfer.model?.name || ''} transfer service`,
                    features: ['Professional Driver', 'Air Conditioning']
                }));
            } else {
                // API'den beklenmeyen format gelirse boş liste döndür
                console.warn('Unexpected API response format:', apiResults);
                return [];
            }
        } catch (error) {
            console.error('Error converting API results:', error);
            return [];
        }
    };

    const handleRangeChange = (values) => {
        setPrices({
            min_price: values[0],
            max_price: values[1]
        });
    };

    const handleDragEnd = useCallback(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        
        debounceTimeout.current = setTimeout(() => {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('minPrice', prices.min_price);
            newParams.set('maxPrice', prices.max_price);
            setSearchParams(newParams);
            setRates({
                minRate: prices.min_price,
                maxRate: prices.max_price
            });
        }, 500);
    }, [prices, searchParams, setSearchParams]);

    const handleVehicleTypeChange = (code) => {
        setSelectedVehicleTypes(prev => {
            const newTypes = prev.includes(code) 
                ? prev.filter(item => item !== code)
                : [...prev, code];
            
            const newParams = new URLSearchParams(searchParams);
            if (newTypes.length > 0) {
                newParams.set('vehicleTypes', newTypes.join(','));
            } else {
                newParams.delete('vehicleTypes');
            }
            setSearchParams(newParams);
            
            return newTypes;
        });
    };

    const handleFeatureChange = (code) => {
        setSelectedFeatures(prev => {
            const newFeatures = prev.includes(code) 
                ? prev.filter(item => item !== code)
                : [...prev, code];
            
            const newParams = new URLSearchParams(searchParams);
            if (newFeatures.length > 0) {
                newParams.set('features', newFeatures.join(','));
            } else {
                newParams.delete('features');
            }
            setSearchParams(newParams);
            
            return newFeatures;
        });
    };

    const handleResetFilters = () => {
        setSelectedVehicleTypes([]);
        setSelectedFeatures([]);
        setRates({
            minRate: 0,
            maxRate: 500
        });
        setPrices({
            min_price: 0,
            max_price: 500
        });

        const url = new URLSearchParams(searchParams);
        url.delete('vehicleTypes');
        url.delete('features');
        url.delete('minPrice');
        url.delete('maxPrice');
        setSearchParams(url);
    };

    const handleViewDetails = (transfer) => {
        const params = new URLSearchParams();
        const transferId = transfer.vehicleId || transfer.id;
        params.set('id', transferId);
        params.set('type', formParams.type);
        params.set('pickup', formParams.pickup);
        params.set('dropoff', formParams.dropoff);
        params.set('date', formParams.date);
        if (formParams.returnDate) params.set('returnDate', formParams.returnDate);
        params.set('adults', formParams.adults);
        params.set('children', formParams.children);
        if (transfer.offerKey) params.set('offerKey', transfer.offerKey);

        navigate(`/transfer-detail/${transferId}?${params.toString()}`);
    };

    const StaticGoogleMap = () => {
        const mapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=41.0082,28.9784&zoom=12&size=600x300&maptype=roadmap&markers=color:red%7C41.0082,28.9784&key=${config.API_KEY}`;

        return (
            <img 
                src={mapUrl} 
                alt="Transfer Route Map" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
        );
    };

    // Handle transfer booking
    const handleTransferBooking = (transferData) => {
        console.log('Booking transfer:', transferData);
        
        // Transfer booking işlemi için detail sayfasına yönlendir
        const transferId = transferData.vehicleId || transferData.id;
        navigate(`/transfers/${transferId}?offerKey=${transferData.offerKey}`, {
            state: {
                transferData: transferData,
                searchParams: {
                    pickup: pickupLocation,
                    dropoff: dropoffLocation,
                    formParams: formParams
                }
            }
        });
    };

    // Cart functions
    const handleAddToCart = (cartItem) => {
        console.log('Adding transfer to cart:', cartItem);
        
        // Get existing cart from localStorage
        const existingCart = JSON.parse(localStorage.getItem('transfer_cart')) || { data: [] };
        
        // Check if item already exists
        const existingItemIndex = existingCart.data.findIndex(item => 
            item.offerKey === cartItem.offerKey && 
            item.vehicleId === cartItem.vehicleId
        );
        
        if (existingItemIndex > -1) {
            Swal.fire({
                title: 'Item Already in Cart',
                text: 'This transfer with the same configuration is already in your cart',
                icon: 'info',
                confirmButtonText: 'OK'
            });
            return;
        }

        // Add new item to cart
        existingCart.data.push(cartItem);
        
        // Save to localStorage
        localStorage.setItem('transfer_cart', JSON.stringify(existingCart));
        
        // Update cart summary
        updateCartSummary();
    };

    const updateCartSummary = () => {
        const cart = JSON.parse(localStorage.getItem('transfer_cart')) || { data: [] };
        
        const totalItems = cart.data.length;
        const totalPrice = cart.data.reduce((sum, item) => sum + parseFloat(item.totalPrice), 0);
        
        setCartSummary({
            totalItems: totalItems,
            items: cart.data,
            totalPrice: totalPrice
        });
    };

    const handleClearCart = async () => {
        const result = await Swal.fire({
            title: 'Clear Cart',
            text: 'Are you sure you want to clear your cart?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, clear it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            localStorage.setItem('transfer_cart', JSON.stringify({ data: [] }));
            setCartSummary({ 
                totalItems: 0, 
                items: [],
                totalPrice: 0
            });
            Swal.fire('Cleared!', 'Your cart has been cleared.', 'success');
        }
    };

    const handleProceedToCart = () => {
        const userData = JSON.parse(localStorage.getItem('user_data'));

        if (!userData?.token) {
            Swal.fire({
                title: 'Please Login',
                text: 'You need to login to proceed to checkout',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        if (cartSummary.totalItems === 0) {
            Swal.fire({
                title: 'Empty Cart',
                text: 'Your cart is empty',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        navigate('/cart');
    };

    // Initialize cart summary on component mount
    useEffect(() => {
        updateCartSummary();
        
        // Listen for cart changes
        const handleStorageChange = () => {
            updateCartSummary();
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Handle pickup location selection
    const handlePickupLocationSelected = (placeData) => {
        setPickupLocation(placeData);
        setFormParams(prev => ({
            ...prev,
            pickup: placeData.name
        }));
        
        // Update URL params with all location data
        const newParams = new URLSearchParams(searchParams);
        newParams.set('pickup', placeData.name);
        newParams.set('pickupLat', placeData.lat);
        newParams.set('pickupLng', placeData.lng);
        newParams.set('pickupType', placeData.locationType);
        setSearchParams(newParams);
    };

    // Handle dropoff location selection
    const handleDropoffLocationSelected = (placeData) => {
        setDropoffLocation(placeData);
        setFormParams(prev => ({
            ...prev,
            dropoff: placeData.name
        }));
        
        // Update URL params with all location data
        const newParams = new URLSearchParams(searchParams);
        newParams.set('dropoff', placeData.name);
        newParams.set('dropoffLat', placeData.lat);
        newParams.set('dropoffLng', placeData.lng);
        newParams.set('dropoffType', placeData.locationType);
        setSearchParams(newParams);
    };

    // Handle form input changes - SADECE STATE GÜNCELLE, URL'yi değil
    const handleFormChange = (field, value) => {
        setFormParams(prev => ({
            ...prev,
            [field]: value
        }));
        
        // URL params'ı güncelleme - sadece search submit'te yap
        // const newParams = new URLSearchParams(searchParams);
        // newParams.set(field, value);
        // setSearchParams(newParams);
    };

    // Handle search form submit
    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        
        if (!pickupLocation.name || !dropoffLocation.name) {
            Swal.fire({
                title: 'Missing Information',
                text: 'Please select both pickup and dropoff locations',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        // Koordinat kontrolü
        if (!pickupLocation.lat || !pickupLocation.lng) {
            Swal.fire({
                title: 'Invalid Pickup Location',
                text: 'Please select a valid pickup location from the suggestions',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        if (!dropoffLocation.lat || !dropoffLocation.lng) {
            Swal.fire({
                title: 'Invalid Dropoff Location',
                text: 'Please select a valid dropoff location from the suggestions',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }
        
        setIsLoading(true);
        setError(null);
        
        try {
            // API için parametreleri hazırla
            const searchParams = {
                transferType: formParams.type,
                fromCoordinates: {
                    lat: pickupLocation.lat,
                    lng: pickupLocation.lng
                },
                toCoordinates: {
                    lat: dropoffLocation.lat,
                    lng: dropoffLocation.lng
                },
                departureDate: formParams.date,
                returnDate: formParams.type === 'round-trip' ? formParams.returnDate : null,
                adults: formParams.adults,
                children: formParams.children
            };

            // API çağrısı yap
            const transferResults = await transferService.searchTransfers(searchParams);
            
            // API sonuçlarını state'e kaydet
            setApiTransferResults(transferResults);
            
            // Search yapıldığında URL'yi güncelle
            const newParams = new URLSearchParams(searchParams);
            Object.keys(formParams).forEach(key => {
                newParams.set(key, formParams[key]);
            });
            setSearchParams(newParams);
            
        } catch (error) {
            console.error('Manual search error:', error);
            
            setError(error.message);
            setTransferList([]); // Boş liste göster
            
            Swal.fire({
                title: 'Search Error',
                text: error.message || 'An error occurred while searching transfers. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
            
        } finally {
            setIsLoading(false);
        }
    };

    const TransferSearchForm = () => {
        const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
        const [passengersModalOpen, setPassengersModalOpen] = useState(false);
        const passengersRef = useRef(null);

        useEffect(() => {
            const handleResize = () => {
                setIsMobile(window.innerWidth <= 768);
            };
            window.addEventListener("resize", handleResize);
            return () => window.removeEventListener("resize", handleResize);
        }, []);

        // Handle outside click for passengers dropdown
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (passengersRef.current && !passengersRef.current.contains(event.target)) {
                    setPassengersModalOpen(false);
                }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, []);

        return (
            <div className="transfer-search-form-modern">
                <form onSubmit={handleSearchSubmit}>
                    {/* Transfer Type Selection - Modern Style */}
                    <div className="d-flex flex-column" style={{ padding: '0px', textAlign: 'start', marginBottom: '20px' }}>
                        <div className="transfer-type-row">
                            <div className={`transfer-type-option ${formParams.type === 'one-way' ? 'active' : ''}`}>
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="transferType"
                                    id="oneWaySearch"
                                    value="one-way"
                                    checked={formParams.type === 'one-way'}
                                    onChange={(e) => handleFormChange('type', e.target.value)}
                                />
                                <label className="form-check-label fw-medium" htmlFor="oneWaySearch">
                                    <i className="isax isax-arrow-right me-1" style={{ fontSize: '12px' }}></i>One Way
                                </label>
                            </div>
                            <div className={`transfer-type-option ${formParams.type === 'round-trip' ? 'active' : ''}`}>
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="transferType"
                                    id="roundTripSearch"
                                    value="round-trip"
                                    checked={formParams.type === 'round-trip'}
                                    onChange={(e) => handleFormChange('type', e.target.value)}
                                />
                                <label className="form-check-label fw-medium" htmlFor="roundTripSearch">
                                    <i className="isax isax-arrow-swap-horizontal me-1" style={{ fontSize: '12px' }}></i>Round Trip
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Modern Form Fields */}
                    <div className="d-flex flex-column">
                        <div className={`d-flex form-info ${isMobile ? 'mobile' : 'desktop'}`}>
                            {/* Pickup Location */}
                            <div className="form-item dropdown transfer-location-item">
                                <div className="form-container">
                                    <div className="form-icon">
                                        <i className="isax isax-location"></i>
                                    </div>
                                    
                                    <div className="form-content">
                                        <label className="form-label">
                                            Pickup Location
                                        </label>
                                        <GoogleMapsLoader apiKey={config.API_KEY}>
                                            <TransferAutocomplete
                                                placeholder="From where?"
                                                onPlaceSelected={handlePickupLocationSelected}
                                                defaultValue={pickupLocation.name}
                                                id="pickup-location"
                                                name="pickup"
                                                style={{
                                                    border: 'none',
                                                    padding: '0',
                                                    fontSize: '14px',
                                                    fontWeight: '500',
                                                    backgroundColor: 'transparent',
                                                    outline: 'none',
                                                    boxShadow: 'none',
                                                    color: '#2c3e50',
                                                    width: '100%'
                                                }}
                                            />
                                        </GoogleMapsLoader>
                                    </div>
                                </div>
                            </div>

                            {/* Dropoff Location */}
                            <div className="form-item dropdown transfer-location-item">
                                <div className="form-container">
                                    <div className="form-icon">
                                        <i className="isax isax-location"></i>
                                    </div>
                                    
                                    <div className="form-content">
                                        <label className="form-label">
                                            Dropoff Location
                                        </label>
                                        <GoogleMapsLoader apiKey={config.API_KEY}>
                                            <TransferAutocomplete
                                                placeholder="To where?"
                                                onPlaceSelected={handleDropoffLocationSelected}
                                                defaultValue={dropoffLocation.name}
                                                id="dropoff-location"
                                                name="dropoff"
                                                style={{
                                                    border: 'none',
                                                    padding: '0',
                                                    fontSize: '14px',
                                                    fontWeight: '500',
                                                    backgroundColor: 'transparent',
                                                    outline: 'none',
                                                    boxShadow: 'none',
                                                    color: '#2c3e50',
                                                    width: '100%'
                                                }}
                                            />
                                        </GoogleMapsLoader>
                                    </div>
                                </div>
                            </div>

                            {/* Transfer Date */}
                            <div className="form-item" style={{ minWidth: formParams.type === 'round-trip' ? '200px' : '160px' }}>
                                <div className="form-container">
                                    <div className="form-icon">
                                        <i className="isax isax-calendar"></i>
                                    </div>
                                    
                                    <div className="form-content">
                                        <label className="form-label">
                                            {formParams.type === 'one-way' ? 'Transfer Date' : 'Departure'}
                                        </label>
                                        <input 
                                            type="date"
                                            className="transfer-date-input"
                                            value={formParams.date}
                                            min={new Date().toISOString().split('T')[0]}
                                            onChange={(e) => {
                                                const newDate = e.target.value;
                                                handleFormChange('date', newDate);
                                                // Round trip ise return date'i otomatik ayarla
                                                if (formParams.type === 'round-trip') {
                                                    const nextDay = new Date(newDate);
                                                    nextDay.setDate(nextDay.getDate() + 1);
                                                    handleFormChange('returnDate', nextDay.toISOString().split('T')[0]);
                                                }
                                            }}
                                            onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Return Date - only show for round trip */}
                            {formParams.type === 'round-trip' && (
                                <div className="form-item" style={{ minWidth: '160px' }}>
                                    <div className="form-container">
                                        <div className="form-icon">
                                            <i className="isax isax-calendar"></i>
                                        </div>
                                        
                                        <div className="form-content">
                                            <label className="form-label">
                                                Return
                                            </label>
                                            <input 
                                                type="date"
                                                className="transfer-date-input"
                                                value={formParams.returnDate}
                                                min={formParams.date || new Date().toISOString().split('T')[0]}
                                                onChange={(e) => handleFormChange('returnDate', e.target.value)}
                                                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Passengers - Modern Dropdown */}
                            <div className="form-item dropdown" style={{ 
                                minWidth: '160px',
                                position: 'relative',
                                zIndex: 9997
                            }}>
                                <div className="form-container">
                                    <div className="form-icon">
                                        <i className="isax isax-profile-2user"></i>
                                    </div>
                                    
                                    <div className="form-content">
                                        <label className="form-label">
                                            Passengers
                                        </label>
                                        <div 
                                            className="form-control"
                                            onClick={() => setPassengersModalOpen(!passengersModalOpen)}
                                            style={{
                                                border: 'none',
                                                padding: '0',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                backgroundColor: 'transparent',
                                                outline: 'none',
                                                boxShadow: 'none',
                                                color: '#2c3e50',
                                                cursor: 'pointer',
                                                width: '100%',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <span>
                                                {formParams.adults + formParams.children} Passengers
                                            </span>
                                            <i className="isax isax-arrow-down-1" style={{ 
                                                color: '#64748b', 
                                                fontSize: '14px' 
                                            }}></i>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className="transfer-passengers-dropdown"
                                    ref={passengersRef}
                                    style={{
                                        display: passengersModalOpen ? "block" : "none"
                                    }}
                                >
                                    {/* Header */}
                                    <div className="transfer-passengers-header">
                                        <div className="header-title">
                                            <i className="isax isax-profile-2user"></i>
                                            Passengers
                                        </div>
                                    </div>

                                    {/* Passengers section */}
                                    <div className="transfer-passengers-section">
                                        {/* Adults */}
                                        <div className="transfer-passenger-row">
                                            <div className="transfer-passenger-label">
                                                <i className="isax isax-user"></i>
                                                Adults
                                            </div>
                                            <div className="transfer-passenger-counter">
                                                <button
                                                    type="button"
                                                    onClick={() => handleFormChange('adults', Math.max(1, formParams.adults - 1))}
                                                    className="transfer-passenger-btn"
                                                    disabled={formParams.adults <= 1}
                                                >
                                                    −
                                                </button>
                                                <span className="transfer-passenger-count">
                                                    {formParams.adults}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleFormChange('adults', formParams.adults + 1)}
                                                    className="transfer-passenger-btn"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>

                                        {/* Children */}
                                        <div className="transfer-passenger-row">
                                            <div className="transfer-passenger-label">
                                                <i className="isax isax-user-octagon"></i>
                                                Children
                                            </div>
                                            <div className="transfer-passenger-counter">
                                                <button
                                                    type="button"
                                                    onClick={() => handleFormChange('children', Math.max(0, formParams.children - 1))}
                                                    className="transfer-passenger-btn"
                                                    disabled={formParams.children <= 0}
                                                >
                                                    −
                                                </button>
                                                <span className="transfer-passenger-count">
                                                    {formParams.children}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleFormChange('children', formParams.children + 1)}
                                                    className="transfer-passenger-btn"
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Submit Button - Desktop'ta aynı satırda, Mobile'da alt satırda */}
                            {!isMobile && (
                                <div className="submit-btn-desktop">
                                    <button type="submit" className="search-btn desktop">
                                        <i className="isax isax-search-normal"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        {/* Submit Button - Sadece Mobile'da alt satırda */}
                        {isMobile && (
                            <div className="submit-btn-mobile">
                                <div className="btn-container">
                                    <button type="submit" className="search-btn mobile">
                                        <i className="isax isax-search-normal"></i>
                                        <span className="ms-2">Search Transfers</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </form>
            </div>
        );
    };

    return (
        <Layout>
            <div className="breadcrumb-bar breadcrumb-bg-01 text-center">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 col-12">
                            <h2 className="breadcrumb-title mb-2">Transfers</h2>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb justify-content-center mb-0">
                                    <li className="breadcrumb-item"><a href="/"><i className="isax isax-home5"></i></a></li>
                                    <li className="breadcrumb-item">Transfers</li>
                                    <li className="breadcrumb-item active" aria-current="page">Transfer Lists</li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container">
                    <div className="card">
                        <div className="card-body">
                            <div className="banner-form">
                                <TransferSearchForm />
                            </div>
                        </div>
                    </div>


                    
                    {error && (
                        <div className="alert alert-danger mt-3">
                            <i className="isax isax-warning-2 me-2"></i>
                            <strong>Error:</strong> {error}
                        </div>
                    )}

                    <div className="row d-flex flex-column-reverse flex-xl-row">
                        <div className="col-xl-3 col-lg-3 theiaStickySidebar">
                            <div style={{ height: '150px', position: 'relative', marginBottom: '20px' }}>
                                <GoogleMapsLoader apiKey={config.API_KEY}>
                                    <StaticGoogleMap />
                                </GoogleMapsLoader>
                                <button
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        backgroundColor: '#007bff',
                                        color: '#fff',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                    }}
                                >
                                    View Route
                                </button>
                            </div>

                            <div className="card filter-sidebar mb-lg-0 mb-4">
                                <div className="card-body p-0">
                                    <form>
                                        <div className="accordion accordion-list">
                                            {/* Reset Filters Button */}
                                            <div className="p-3 border-bottom mt-8">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary w-100"
                                                    onClick={handleResetFilters}
                                                >
                                                    <i className="isax isax-refresh me-2"></i>
                                                    Reset Filters
                                                </button>
                                            </div>

                                            {/* Price Range Filter */}
                                            <div className="accordion-item border-bottom p-3">
                                                <div className="accordion-header">
                                                    <div className="accordion-button p-0" data-bs-toggle="collapse"
                                                        data-bs-target="#accordion-price" aria-expanded="true"
                                                        aria-controls="accordion-price" role="button">
                                                        <i className="isax isax-coin text-primary me-2"></i>Price Range
                                                    </div>
                                                </div>
                                                <div id="accordion-price" className="accordion-collapse show collapse">
                                                    <div className="accordion-body d-flex flex-row">
                                                        <div style={{ paddingTop: "20px", width: "100%", margin: "0 auto" }}>
                                                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                                                                <span style={{ fontWeight: 'bold', fontSize: '12px' }}>0 EUR</span>
                                                                <span style={{ fontWeight: 'bold', fontSize: '12px' }}>500 EUR</span>
                                                            </div>
                                                            <RangeSlider
                                                                min={0}
                                                                max={500}
                                                                value={[prices.min_price, prices.max_price]}
                                                                onInput={handleRangeChange}
                                                                onThumbDragEnd={handleDragEnd}
                                                                thumbsDisabled={[false, false]}
                                                                rangeSlideDisabled={false}
                                                            />
                                                            <p style={{ textAlign: 'center' }} className='mt-4 mb-4'>
                                                                <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{prices.min_price} EUR</span> - 
                                                                <span style={{ fontWeight: 'bold', fontSize: '14px' }}> {prices.max_price} EUR</span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Vehicle Types Filter */}
                                            <div className="accordion-item border-bottom p-3">
                                                <div className="accordion-header">
                                                    <div className="accordion-button p-0" data-bs-toggle="collapse"
                                                        data-bs-target="#accordion-vehicle-types" aria-expanded="true"
                                                        aria-controls="accordion-vehicle-types" role="button">
                                                        <FaCar className="text-primary me-2"/>
                                                        Vehicle Types
                                                    </div>
                                                </div>
                                                <div id="accordion-vehicle-types" className="accordion-collapse show collapse">
                                                    <div className="accordion-body">
                                                        {(vehicleTypesLess ? vehicleTypes.slice(0, 5) : vehicleTypes).map((item, index) => (
                                                            <div key={`vehicle-type-${index}`} className="form-check d-flex align-items-center mb-2 ps-0">
                                                                <input
                                                                    className="form-check-input ms-0 mt-0"
                                                                    name="vehicleType"
                                                                    type="checkbox"
                                                                    style={{ border: 'solid gray', borderRadius: '4px', width: '17px', height: '17px', cursor: 'pointer' }}
                                                                    id={`vehicle-type-${index}`}
                                                                    value={item.code}
                                                                    checked={selectedVehicleTypes?.includes(item.code)}
                                                                    onChange={() => handleVehicleTypeChange(item.code)}
                                                                />
                                                                <label className="form-check-label ms-2" htmlFor={`vehicle-type-${index}`}>
                                                                    <span className="rating d-flex align-items-center">
                                                                        <span className="ms-2 text-capitalize">{item.name}</span>
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        ))}
                                                        {vehicleTypes.length > 5 && (
                                                            <div className="mt-3">
                                                                <button 
                                                                    className="btn btn-link p-0 text-primary" 
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        setVehicleTypesLess((prev) => !prev);
                                                                    }}
                                                                >
                                                                    {vehicleTypesLess ? "See More" : "See Less"}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Features Filter */}
                                            <div className="accordion-item border-bottom p-3">
                                                <div className="accordion-header">
                                                    <div className="accordion-button p-0" data-bs-toggle="collapse"
                                                        data-bs-target="#accordion-features" aria-expanded="true"
                                                        aria-controls="accordion-features" role="button">
                                                        <i className="isax isax-star text-primary me-2"></i>
                                                        Features
                                                    </div>
                                                </div>
                                                <div id="accordion-features" className="accordion-collapse show collapse">
                                                    <div className="accordion-body">
                                                        {(featuresLess ? features.slice(0, 5) : features).map((feature, index) => (
                                                            <div key={`feature-${index}`} className="form-check d-flex align-items-center mb-2 ps-0">
                                                                <input
                                                                    className="form-check-input ms-0 mt-0"
                                                                    type="checkbox"
                                                                    style={{ border: 'solid gray', borderRadius: '4px', width: '17px', height: '17px', cursor: 'pointer' }}
                                                                    id={`feature-${index}`}
                                                                    value={feature.code}
                                                                    checked={selectedFeatures?.includes(feature.code)}
                                                                    onChange={() => handleFeatureChange(feature.code)}
                                                                />
                                                                <label className="form-check-label ms-2 text-capitalize" htmlFor={`feature-${index}`}>
                                                                    {feature.name}
                                                                </label>
                                                            </div>
                                                        ))}
                                                        {features.length > 5 && (
                                                            <div className="mt-3">
                                                                <button 
                                                                    type="button"
                                                                    className="btn btn-link p-0 text-primary" 
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        setFeaturesLess(prev => !prev);
                                                                    }}
                                                                >
                                                                    {featuresLess ? "See More" : "See Less"}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-9 col-lg-9">
                            <div className="d-flex align-items-center justify-content-between flex-wrap">
                                <div className="d-flex align-items-center flex-wrap">
                                    <div className="dropdown mb-3">
                                        <a href="javascript:void(0);" className="dropdown-toggle py-2" data-bs-toggle="dropdown" aria-expanded="false">
                                            <span className="fw-medium text-gray-9">Sort By : {sortOption.column === 'default' ? 'Recommended' : sortOption.column === 'price' ? 'Price' : sortOption.column === 'name' ? 'Name' : ''}  {sortOption.type === 'asc' ? '- Ascending' : sortOption.type === 'desc' ? '- Descending' : ''}</span>
                                        </a>
                                        <div className="dropdown-menu dropdown-sm">
                                            <h6 className="fw-medium fs-16 mb-3">Sort By</h6>
                                            <div className="form-check d-flex align-items-center ps-0 mb-2">
                                                <input
                                                    className="form-check-input ms-0 mt-0"
                                                    name="sort"
                                                    style={{ border: 'solid gray', borderRadius: '4px', width: '17px', height: '17px', cursor: 'pointer' }}
                                                    type="radio"
                                                    id="sort-default"
                                                    value="default"
                                                    checked={sortOption.type === 'default'}
                                                    onChange={() => setSortOption({type: 'default', column: 'default'})}
                                                />
                                                <label className="form-check-label ms-2" htmlFor="sort-default">Recommended</label>
                                            </div>
                                            <div className="form-check d-flex align-items-center ps-0 mb-2">
                                                <input
                                                    className="form-check-input ms-0 mt-0"
                                                    name="sort"
                                                    style={{ border: 'solid gray', borderRadius: '4px', width: '17px', height: '17px', cursor: 'pointer' }}
                                                    type="radio"
                                                    id="sort-price-asc"
                                                    value="price-asc"
                                                    checked={sortOption.type === 'asc' && sortOption.column === 'price'}
                                                    onChange={() => setSortOption({type: 'asc', column: 'price'})}
                                                />
                                                <label className="form-check-label ms-2" htmlFor="sort-price-asc">Price (Low to High)</label>
                                            </div>
                                            <div className="form-check d-flex align-items-center ps-0 mb-2">
                                                <input
                                                    className="form-check-input ms-0 mt-0"
                                                    name="sort"
                                                    style={{ border: 'solid gray', borderRadius: '4px', width: '17px', height: '17px', cursor: 'pointer' }}
                                                    type="radio"
                                                    id="sort-price-desc"
                                                    value="price-desc"
                                                    checked={sortOption.type === 'desc' && sortOption.column === 'price'}
                                                    onChange={() => setSortOption({type: 'desc', column: 'price'})}
                                                />
                                                <label className="form-check-label ms-2" htmlFor="sort-price-desc">Price (High to Low)</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modern API Results Info */}
                            {apiTransferResults && apiTransferResults.success && (
                                <div className="card mb-3" style={{
                                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(147, 51, 234, 0.08) 100%)',
                                    border: '1px solid rgba(59, 130, 246, 0.2)',
                                    borderRadius: '12px'
                                }}>
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-start gap-3">
                                            <div className="d-flex align-items-center justify-content-center" style={{
                                                width: '40px',
                                                height: '40px',
                                                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                                borderRadius: '10px',
                                                flexShrink: 0
                                            }}>
                                                <i className="isax isax-search-normal" style={{ color: '#3b82f6', fontSize: '20px' }}></i>
                                            </div>
                                            
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                    <h6 className="mb-0 fw-semibold" style={{ color: '#1e40af' }}>
                                                        {apiTransferResults.data?.total_count || transferList.length} Transfer{(apiTransferResults.data?.total_count || transferList.length) !== 1 ? 's' : ''} Found
                                                    </h6>
                                                    <span className="badge" style={{ 
                                                        backgroundColor: 'rgba(34, 197, 94, 0.1)', 
                                                        color: '#16a34a',
                                                        fontSize: '11px',
                                                        padding: '4px 8px'
                                                    }}>
                                                        {apiTransferResults.data?.transfer_type?.replace('_', ' ').toUpperCase()}
                                                    </span>
                                                </div>
                                                
                                                {apiTransferResults.data?.search_params && (
                                                    <div className="row g-3 small">
                                                        <div className="col-12">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <i className="isax isax-location" style={{ color: '#6b7280', fontSize: '14px' }}></i>
                                                                <span style={{ color: '#374151', fontWeight: '500' }}>
                                                                    {(() => {
                                                                        const from = apiTransferResults.data.search_params.from_formatted_address;
                                                                        const to = apiTransferResults.data.search_params.to_formatted_address;
                                                                        
                                                                        // Adresi daha basit formata çevir
                                                                        const simplifyAddress = (address) => {
                                                                            if (!address) return '';
                                                                            
                                                                            // İlk olarak virgül ile bölüp ana kısmı al
                                                                            const parts = address.split(',').map(part => part.trim());
                                                                            
                                                                            // Eğer Airport, Hotel gibi ana yer isimleri varsa onları kullan
                                                                            const firstPart = parts[0];
                                                                            
                                                                            // Ana yer ismini bul (Airport, Hotel, vs.)
                                                                            if (firstPart.includes('Airport') || 
                                                                                firstPart.includes('Hotel') || 
                                                                                firstPart.includes('Station') || 
                                                                                firstPart.length < 30) {
                                                                                return firstPart;
                                                                            }
                                                                            
                                                                            // Uzun ise şehir/bölge ismini kullan
                                                                            const cityPart = parts.find(part => 
                                                                                part.includes('İstanbul') || 
                                                                                part.includes('Ankara') || 
                                                                                part.includes('İzmir') ||
                                                                                part.includes('Antalya') ||
                                                                                part.includes('Bursa')
                                                                            );
                                                                            
                                                                            if (cityPart) {
                                                                                // Şehir ismindeki ilçe bilgisini al
                                                                                const district = cityPart.split('/')[0].trim();
                                                                                return district;
                                                                            }
                                                                            
                                                                            // Son çare olarak ilk kısmı kısalt
                                                                            return firstPart.length > 25 ? firstPart.substring(0, 25) + '...' : firstPart;
                                                                        };
                                                                        
                                                                        return `${simplifyAddress(from)} → ${simplifyAddress(to)}`;
                                                                    })()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <i className="isax isax-calendar" style={{ color: '#6b7280', fontSize: '14px' }}></i>
                                                                <span style={{ color: '#374151' }}>
                                                                    {apiTransferResults.data.search_params.departure_date}
                                                                    {apiTransferResults.data.search_params.return_date && (
                                                                        <span className="text-muted"> - {apiTransferResults.data.search_params.return_date}</span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <i className="isax isax-profile-2user" style={{ color: '#6b7280', fontSize: '14px' }}></i>
                                                                <span style={{ color: '#374151' }}>
                                                                    {apiTransferResults.data.filters?.adult || 0} Adults, {apiTransferResults.data.filters?.child || 0} Children
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        {apiTransferResults.data?.pagination && (
                                                            <div className="col-12">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <i className="isax isax-document" style={{ color: '#6b7280', fontSize: '14px' }}></i>
                                                                    <span style={{ color: '#6b7280', fontSize: '12px' }}>
                                                                        Page {apiTransferResults.data.pagination.page} of {apiTransferResults.data.pagination.totalPages} 
                                                                        ({apiTransferResults.data.pagination.currentCount} of {apiTransferResults.data.pagination.totalCount} results)
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="transfer-list">
                                <div className="row justify-content-center">
                                    <div className="col-md-12">
                                        {isLoading ? (
                                            <div className="d-flex flex-column align-items-center justify-content-center" style={{ marginBlock: '100px' }}>
                                                <img src="/assets/gif/loading.gif" alt="Loading" style={{ width: "80px", height: "80px" }} />
                                                <p className="mt-3 mb-0">Loading Transfers...</p>
                                            </div>
                                        ) : error ? (
                                            <div className="alert alert-danger" role="alert">
                                                {error}
                                            </div>
                                                                ) : error ? (
                            <div className="card mb-4">
                                <div className="card-body text-center py-5">
                                    <div className="mb-4">
                                        <i className="isax isax-warning-2" style={{ fontSize: "48px", color: "#ef4444" }}></i>
                                    </div>
                                    <h3 className="mb-3 text-danger">Search Error</h3>
                                    <p className="text-muted mb-4">
                                        {error}
                                    </p>
                                    <button 
                                        className="btn btn-primary" 
                                        onClick={() => {
                                            setError(null);
                                            if (pickupLocation.lat && dropoffLocation.lat && formParams.date) {
                                                document.querySelector('form').dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                                            }
                                        }}
                                    >
                                        <i className="isax isax-refresh me-2"></i>
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        ) : transferList && transferList.length > 0 ? (
                            <>
                                {transferList.map((transfer, index) => (
                                    <TransferListCard 
                                        key={transfer.vehicleId || transfer.id || index}
                                        transfer={transfer}
                                        onBookNow={handleTransferBooking}
                                        onAddToCart={handleAddToCart}
                                    />
                                ))}
                            </>
                        ) : (
                            <div className="card mb-4">
                                <div className="card-body text-center py-5">
                                    <div className="mb-4">
                                        <i className="isax isax-search-normal" style={{ fontSize: "48px", color: "#6c757d" }}></i>
                                    </div>
                                    <h3 className="mb-3">No Data Available</h3>
                                    <p className="text-muted mb-4">
                                        Please fill in the search form above to find available transfers.
                                    </p>
                                </div>
                            </div>
                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fixed Cart Footer */}
            {cartSummary.totalItems > 0 && (
                <div className="fixed-cart-footer">
                    <div className="cart-summary">
                        <FaShoppingCart className="cart-icon" />
                        <div className="summary-text">
                            <span className="total-items">{cartSummary.totalItems} transfer{cartSummary.totalItems > 1 ? 's' : ''} selected</span>
                            <div className="transfer-items">
                                {cartSummary.items.slice(0, 2).map((item, index) => (
                                    <span key={index} className="transfer-item">
                                        {item.vehicleType} {item.vehicleCategory} - {item.routes?.[0]?.from?.name || item.pickup} → {item.routes?.[0]?.to?.name || item.dropoff}
                                    </span>
                                ))}
                                {cartSummary.items.length > 2 && (
                                    <span className="more-items">+{cartSummary.items.length - 2} more</span>
                                )}
                            </div>
                            <div className="total-price">
                                Total: {cartSummary.totalPrice.toFixed(2)} {cartSummary.items[0]?.currencySymbol || cartSummary.items[0]?.currency?.symbol || '€'}
                            </div>
                        </div>
                    </div>
                    <div className="cart-actions">
                        <button className="btn btn-outline-danger" onClick={handleClearCart}>
                            <FaTrash /> Clear Cart
                        </button>
                        <button className="btn btn-primary" onClick={handleProceedToCart}>
                            Go to Cart
                        </button>
                    </div>
                </div>
            )}

            {/* Sticky Cart Styles */}
            <style jsx>{`
                /* Fixed Cart Footer Styles */
                .fixed-cart-footer {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background: linear-gradient(135deg, 
                        rgba(255, 255, 255, 0.98) 0%, 
                        rgba(248, 249, 250, 0.95) 100%);
                    backdrop-filter: blur(20px);
                    padding: 15px 20px;
                    box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.15);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    z-index: 1000;
                    border-top: 1px solid rgba(255, 180, 120, 0.3);
                    transition: all 0.3s ease;
                }

                .fixed-cart-footer:hover {
                    box-shadow: 0 -12px 40px rgba(0, 0, 0, 0.2);
                }

                .cart-summary {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                }

                .cart-summary .cart-icon {
                    font-size: 28px;
                    color: rgba(220, 120, 60, 0.9);
                    padding: 12px;
                    background: linear-gradient(135deg, 
                        rgba(255, 235, 210, 0.8) 0%, 
                        rgba(255, 220, 185, 0.6) 100%);
                    border-radius: 50%;
                    border: 2px solid rgba(255, 180, 120, 0.4);
                    transition: all 0.3s ease;
                }

                .cart-summary .cart-icon:hover {
                    transform: scale(1.1);
                    color: rgba(210, 110, 50, 1);
                }

                .summary-text {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .total-items {
                    font-weight: 700;
                    font-size: 16px;
                    color: rgba(210, 110, 50, 0.95);
                    letter-spacing: 0.3px;
                }

                .transfer-items {
                    display: flex;
                    gap: 8px;
                    flex-wrap: wrap;
                    max-width: 400px;
                }

                .transfer-item {
                    font-size: 13px;
                    color: #64748b;
                    background: linear-gradient(135deg, 
                        rgba(255, 240, 220, 0.7) 0%, 
                        rgba(255, 230, 200, 0.5) 100%);
                    padding: 4px 10px;
                    border-radius: 12px;
                    border: 1px solid rgba(255, 200, 150, 0.4);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 250px;
                    transition: all 0.2s ease;
                }

                .transfer-item:hover {
                    background: linear-gradient(135deg, 
                        rgba(255, 235, 210, 0.8) 0%, 
                        rgba(255, 220, 185, 0.6) 100%);
                }

                .more-items {
                    font-size: 13px;
                    color: rgba(220, 120, 60, 0.8);
                    background: linear-gradient(135deg, 
                        rgba(255, 220, 185, 0.6) 0%, 
                        rgba(255, 205, 160, 0.4) 100%);
                    padding: 4px 10px;
                    border-radius: 12px;
                    border: 1px solid rgba(255, 180, 120, 0.5);
                    font-weight: 600;
                }

                .total-price {
                    font-weight: 800;
                    font-size: 18px;
                    color: rgba(210, 110, 50, 0.95);
                    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                    margin-top: 2px;
                }

                .cart-actions {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                }

                .cart-actions .btn {
                    padding: 10px 20px;
                    font-weight: 600;
                    border-radius: 10px;
                    transition: all 0.3s ease;
                    font-size: 14px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    min-width: 120px;
                    justify-content: center;
                }

                .cart-actions .btn-outline-danger {
                    border: 2px solid #ef4444;
                    color: #ef4444;
                    background: rgba(255, 255, 255, 0.9);
                }

                .cart-actions .btn-outline-danger:hover {
                    background: #ef4444;
                    color: white;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.3);
                }

                .cart-actions .btn-primary {
                    background: linear-gradient(135deg, 
                        rgba(240, 130, 70, 0.9) 0%, 
                        rgba(220, 110, 50, 0.95) 100%);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 15px rgba(220, 120, 60, 0.3);
                }

                .cart-actions .btn-primary:hover {
                    background: linear-gradient(135deg, 
                        rgba(250, 140, 80, 1) 0%, 
                        rgba(230, 120, 60, 1) 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(220, 120, 60, 0.4);
                }

                /* Mobile Responsive */
                @media (max-width: 768px) {
                    .fixed-cart-footer {
                        flex-direction: column;
                        gap: 15px;
                        padding: 12px 15px;
                    }

                    .cart-summary {
                        width: 100%;
                        justify-content: center;
                    }

                    .summary-text {
                        text-align: center;
                        align-items: center;
                    }

                    .transfer-items {
                        justify-content: center;
                        max-width: 100%;
                    }

                    .transfer-item {
                        max-width: 200px;
                        font-size: 12px;
                    }

                    .cart-actions {
                        width: 100%;
                        justify-content: space-between;
                    }

                    .cart-actions .btn {
                        flex: 1;
                        min-width: auto;
                        padding: 12px 16px;
                    }
                }

                @media (max-width: 480px) {
                    .cart-summary .cart-icon {
                        font-size: 24px;
                        padding: 10px;
                    }

                    .total-items {
                        font-size: 14px;
                    }

                    .total-price {
                        font-size: 16px;
                    }

                    .transfer-item {
                        font-size: 11px;
                        padding: 3px 8px;
                        max-width: 150px;
                    }
                }
            `}</style>
        </Layout>
    )
}
